import { BridgeComponent } from '@hotwired/hotwire-native-bridge'
import { csrfToken, refreshCsrfToken } from '../../utils/csrf'

// Skips redundant POSTs. Keyed on user + sign-in + token + status + day: a WKWebView's
// localStorage is shared across accounts and survives signing out (A → B → A left the Device
// attached to B), the sign-in count re-registers an installation that signing out revoked,
// the status makes a permission change reach the server, and the day forces one refresh a day
// so `last_seen_at` stays meaningful.
const STORAGE_KEY = 'listy_push_device'
const today = () => new Date().toISOString().slice(0, 10)
const signature = (userId, signInCount, token, status) =>
  `${userId}:${signInCount}:${token}:${status}:${today()}`

// Statuses under which the system delivers notifications (`UNAuthorizationStatus`;
// `provisional` and `ephemeral` are Apple-only). Any other status shows the settings
// insert. Android maps its POST_NOTIFICATIONS permission into the same vocabulary, so
// this controller serves both shells.
const DELIVERING = ['authorized', 'provisional', 'ephemeral']

// Bridge component for native push notifications — paired with
// ../listy-ios/Listy/Bridge/PushComponent.swift, which documents the wire contract, and
// with its Android twin ../listy-android/…/bridges/PushComponent.kt.
//
// Mounted on <body>, so `sync` runs on every Turbo navigation. Registering through the
// bridge rather than a native network call reuses the already authenticated web session.
//
// The system prompt fires on the first authenticated page load; the settings insert
// (notifications/_device_status) is the only UI, revealed here while notifications are off.
export default class extends BridgeComponent {
  static component = 'push'
  static targets = ['prompt']

  #promptRequested = false
  #status = null
  static values = { userId: String, signInCount: Number, platform: String }

  connect() {
    super.connect()
    this.#sync()
    document.addEventListener('visibilitychange', this.#onVisibilityChange)
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.#onVisibilityChange)
    super.disconnect()
  }

  // Turning notifications on happens in the system settings, which the app cannot observe —
  // it is suspended meanwhile. Coming back is the earliest moment we can notice, and it
  // follows immediately since changing the setting means leaving the app. Without this, the
  // change waited for the next Turbo navigation.
  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#sync()
  }

  #sync() {
    this.send('sync', {}, (message) => this.#onSync(message.data))
  }

  // The native side replies once the push registration is in (the APNs token, the Firebase
  // Installation ID), not when the prompt closes: registering right away, rather than on the
  // next `sync`, covers whoever accepts then leaves the app.
  enable() {
    this.#promptRequested = true
    this.send('requestPermission', {}, (message) => this.#onSync(message.data))
  }

  // The insert's single button. Once denied, the settings are the only way back — neither
  // system shows its prompt twice. While the prompt has never been shown (or the status is
  // unknown), the settings would not list Listy yet: the prompt is what enables
  // notifications.
  openSettings() {
    if (this.#status === 'denied') this.send('openSettings')
    else this.enable()
  }

  #onSync({ token, authorized, status, apnsEnvironment, installationId }) {
    // The flag guards against a failed request answering `notDetermined` again, which would
    // loop.
    if (status === 'notDetermined' && !this.#promptRequested) {
      this.enable()
      return
    }

    this.#status = status
    this.#revealStatus(status)

    // Registered even without a token: those installations are the ones we most want to
    // count, and the row carries the status.
    const registered = signature(this.userIdValue, this.signInCountValue, token, status)
    if (registered === localStorage.getItem(STORAGE_KEY)) return

    // Server-derived, from the framework's own user-agent token: hard-coding 'ios' here
    // would file an Android install under the wrong platform, which is the column the
    // delivery worker routes on. Omitted when unrecognised rather than guessed.
    const device = { installation_id: installationId, authorization_status: status }
    if (this.platformValue) device.platform = this.platformValue
    // `token` only exists once the permission is granted, and both shells nil it otherwise;
    // sent rather than omitted so a permission turned off does not strip a still-valid one.
    // Android has no `apnsEnvironment` to report, and the key is simply absent from its reply.
    if (token) Object.assign(device, { token, apns_environment: apnsEnvironment })

    this.#register(device).then((response) => {
      // 204, not `response.ok`: `fetch` follows redirects, so an expired session returns the
      // sign-in page as 200 and would be recorded as a registration that never happened.
      if (response.status === 204) localStorage.setItem(STORAGE_KEY, registered)
    }).catch(() => {
      // Offline, most likely. Nothing is stored, so the next sync retries on its own.
    })
  }

  // A page left alive in a native tab can outlive a session rotation (sign-in, the
  // NativeApiToken exchange, another tab): its token is then stale and the POST 422s. Nothing
  // being stored, every later `sync` used to replay the same doomed POST and the device was
  // never registered. One retry with a fresh token, not a loop: a second 422 is something else.
  async #register(device) {
    const response = await this.#post(device)
    if (!this.#forgeryRejected(response) || !(await refreshCsrfToken())) return response

    return this.#post(device)
  }

  // The controller's own 422s (invalid params) come back as JSON `{ errors }`; a rejected CSRF
  // token goes through the exceptions app, which renders HTML. A fresh token would not fix the
  // former, so it is not worth a retry.
  #forgeryRejected(response) {
    return response.status === 422 && !response.headers.get('Content-Type')?.includes('json')
  }

  #post(device) {
    return fetch('/devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ device })
    })
  }

  // Shown while notifications are off, hidden once they are delivered — including right
  // after the prompt is accepted.
  #revealStatus(status) {
    if (!this.hasPromptTarget) return

    this.promptTarget.classList.toggle('hidden', DELIVERING.includes(status))
  }
}
